import StorySource from '../../data/story-source';
import LocalStorage from '../../data/local-storage';
import CONFIG from '../../config/app-config';

export default class HomeModel {
  constructor() {
    this.stories = [];
    this.currentOffset = 0;
    this.hasMorePosts = true;
    this.isAuthenticated = false;
    this.user = {};
    this.bookmarks = [];
    this.notifications = [];
    this.currentUserLocation = null;
    this.nearbyDistance = 500;
    this.lastFetchTime = null;
    this.newPostsAvailable = 0;
    this.selectedImage = null;
    this.selectedLocation = null;
    this.videoDevices = [];
    this.currentCameraIndex = 0;
  }

  loadUserState() {
    const token = LocalStorage.getItem('heliaedu_token');
    const userData = LocalStorage.getItem('heliaedu_user');
    
    if (token && userData) {
      this.isAuthenticated = true;
      this.user = JSON.parse(userData);
      
      const bookmarksKey = `heliaedu_user_bookmarks_${this.user.id}`;
      const notificationsKey = `heliaedu_user_notifications_${this.user.id}`;
      const locationKey = `heliaedu_user_location_${this.user.id}`;
      const distanceKey = `heliaedu_user_distance_${this.user.id}`;
      
      this.bookmarks = LocalStorage.getObject(bookmarksKey) || [];
      this.notifications = LocalStorage.getObject(notificationsKey) || [];
      this.currentUserLocation = LocalStorage.getObject(locationKey);
      this.nearbyDistance = parseInt(LocalStorage.getItem(distanceKey) || '500');
      
      this.lastFetchTime = LocalStorage.getItem('heliaedu_user_lastfetch');
    } else {
      this.isAuthenticated = false;
      this.user = {};
      this.bookmarks = [];
      this.notifications = [];
      this.nearbyDistance = parseInt(LocalStorage.getItem('heliaedu_guest_distance') || '500');
    }
  }

  async prepareCamerasForDialog() {
    try {
      this.model.videoDevices = await this.model.prepareCamerasForDialog();
      this.updateFlipCameraButtonVisibility();
    } catch (error) {
      throw new Error('Gagal menyiapkan kamera: ' + error.message);
    }
  }

  flipCamera() {
    if (this.videoDevices.length > 1) {
      this.currentCameraIndex = (this.currentCameraIndex + 1) % this.videoDevices.length;
    }
  }

  async getAllStories(params = {}) {
    const response = await StorySource.getAllStories(params);
    if (!response.error) {
      return response.listStory || [];
    }
    throw new Error(response.message || 'Failed to fetch stories');
  }

  async addStory(storyData) {
    const response = await StorySource.addStory(storyData);
    if (!response.error) {
      return response;
    }
    throw new Error(response.message || 'Failed to add story');
  }

  async getLocationName(lat, lon) {
    try {
      const response = await fetch(
        `https://api.maptiler.com/geocoding/${lon},${lat}.json?key=${CONFIG.MAP_TILER_KEY}`
      );
      const data = await response.json();
      
      if (data.features && data.features.length > 0) {
        const placeName = data.features[0].place_name;
        const parts = placeName.split(',');
        
        if (parts.length >= 2) {
          return `${parts[0].trim()}, ${parts[1].trim()}`;
        }
        return parts[0].trim();
      }
    } catch (error) {
      
    }

    return `${lat.toFixed(3)}, ${lon.toFixed(3)}`;
  }

  async searchLocation(query) {
    try {
      const response = await fetch(
        `https://api.maptiler.com/geocoding/${encodeURIComponent(query)}.json?key=${CONFIG.MAP_TILER_KEY}&limit=5`
      );
      return await response.json();
    } catch (error) {
      throw new Error('Failed to search location');
    }
  }

  async getUserLocation() {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        this.fallbackToIPLocation(resolve, reject);
        return;
      }

      navigator.geolocation.getCurrentPosition(
        (position) => {
          resolve({
            lat: position.coords.latitude,
            lon: position.coords.longitude
          });
        },
        () => {
          this.fallbackToIPLocation(resolve, reject);
        },
        { enableHighAccuracy: true, timeout: 10000, maximumAge: 300000 }
      );
    });
  }

  async fallbackToIPLocation(resolve, reject) {
    try {
      const mapTilerResponse = await fetch(`https://api.maptiler.com/geocoding.json?key=${CONFIG.MAP_TILER_KEY}`);
      if (mapTilerResponse.ok) {
        const mapTilerData = await mapTilerResponse.json();
        if (mapTilerData.features && mapTilerData.features[0]) {
          const [lon, lat] = mapTilerData.features[0].center;
          resolve({ lat, lon });
          return;
        }
      }
    } catch (mapTilerError) {

    }

    try {
      const response = await fetch('https://ipapi.co/json/');
      const data = await response.json();
      if (data.latitude && data.longitude) {
        resolve({
          lat: data.latitude,
          lon: data.longitude
        });
      } else {
        reject(new Error('All fallbacks failed'));
      }
    } catch (ipError) {
      reject(ipError);
    }
  }

  async prepareCamerasForDialog() {
    if (!navigator.mediaDevices || !navigator.mediaDevices.enumerateDevices) {
      throw new Error('Browser tidak mendukung enumerasi perangkat');
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true });
      stream.getTracks().forEach(track => track.stop());
      
      const devices = await navigator.mediaDevices.enumerateDevices();
      this.videoDevices = devices.filter(device => device.kind === 'videoinput');
      
      this.currentCameraIndex = 0;

      if (this.videoDevices.length > 1) {
        const rearCameraIndex = this.videoDevices.findIndex(device => 
          device.label.toLowerCase().includes('back') || 
          device.label.toLowerCase().includes('rear')
        );
        if (rearCameraIndex !== -1) {
          this.currentCameraIndex = rearCameraIndex;
        }
      }
      
      return this.videoDevices;
    } catch (err) {
      throw new Error('Gagal mengakses kamera: ' + err.message);
    }
  }

  async getUserMediaStream(deviceIndex = this.currentCameraIndex) {
    if (this.videoDevices.length === 0) {
      throw new Error('Tidak ada kamera yang tersedia');
    }

    if (deviceIndex >= this.videoDevices.length) {
      deviceIndex = 0;
    }
    
    const selectedDevice = this.videoDevices[deviceIndex];

    const constraints = {
      video: {
        deviceId: { exact: selectedDevice.deviceId },
        width: { ideal: 1280 },
        height: { ideal: 720 }
      },
      audio: false
    };

    return await navigator.mediaDevices.getUserMedia(constraints);
  }

  getMapStyleConfig(isDark = false, layerType = 'streets') {
    let styleUrl;
    switch(layerType) {
      case 'satellite':
        styleUrl = `https://api.maptiler.com/maps/satellite/style.json?key=${CONFIG.MAP_TILER_KEY}`;
        break;
      case 'terrain':
        styleUrl = `https://api.maptiler.com/maps/outdoor-v2/style.json?key=${CONFIG.MAP_TILER_KEY}`;
        break;
      default:
        styleUrl = isDark 
          ? `https://api.maptiler.com/maps/streets-v2-dark/style.json?key=${CONFIG.MAP_TILER_KEY}`
          : `https://api.maptiler.com/maps/streets-v2/style.json?key=${CONFIG.MAP_TILER_KEY}`;
    }
    return styleUrl;
  }

  getLocationMapConfig(isDark = false) {
    return {
      container: 'location-map',
      style: this.getMapStyleConfig(isDark),
      center: [113.9213, -0.7893],
      zoom: 5
    };
  }

  addBookmark(storyId) {
    const story = this.stories.find(s => s.id === storyId);
    
    if (story) {
      const bookmark = {
        storyId: story.id,
        userName: story.name,
        userPhoto: `${CONFIG.AVATAR_BASE_URL}?seed=${encodeURIComponent(story.name)}`,
        description: story.description,
        photoUrl: story.photoUrl,
        lat: story.lat,
        lon: story.lon,
        savedAt: new Date().toISOString()
      };
      
      this.bookmarks.push(bookmark);
      
      const bookmarksKey = `heliaedu_user_bookmarks_${this.user.id}`;
      LocalStorage.setObject(bookmarksKey, this.bookmarks);
      
      return bookmark;
    }
    return null;
  }

  removeBookmark(storyId) {
    const oldLength = this.bookmarks.length;
    this.bookmarks = this.bookmarks.filter(b => b.storyId !== storyId);
    
    if (this.bookmarks.length < oldLength) {
      const bookmarksKey = `heliaedu_user_bookmarks_${this.user.id}`;
      LocalStorage.setObject(bookmarksKey, this.bookmarks);
      return true;
    }
    return false;
  }

  isBookmarked(storyId) {
    return this.bookmarks.some(b => b.storyId === storyId);
  }

  updateLastFetchTime() {
    this.lastFetchTime = new Date().toISOString();
    LocalStorage.setItem('heliaedu_user_lastfetch', this.lastFetchTime);
  }

  checkForNewPosts(stories) {
    if (!this.lastFetchTime || !this.isAuthenticated) return 0;

    const lastFetchDate = new Date(this.lastFetchTime);
    const newStories = stories.filter(story => 
      new Date(story.createdAt) > lastFetchDate
    );

    this.newPostsAvailable = newStories.length;
    return this.newPostsAvailable;
  }

  setStories(stories) {
    this.stories = stories;
  }

  appendStories(stories) {
    this.stories = [...this.stories, ...stories];
  }

  setSelectedImage(file) {
    this.selectedImage = file;
  }

  getSelectedImage() {
    return this.selectedImage;
  }

  clearSelectedImage() {
    this.selectedImage = null;
  }

  setSelectedLocation(location) {
    this.selectedLocation = location;
  }

  getSelectedLocation() {
    return this.selectedLocation;
  }

  clearSelectedLocation() {
    this.selectedLocation = null;
  }

  getFormData(description) {
    return {
      description,
      photo: this.selectedImage,
      lat: this.selectedLocation ? this.selectedLocation.lat : null,
      lon: this.selectedLocation ? this.selectedLocation.lon : null
    };
  }

  getLocationMapConfig() {
    return {
      container: 'location-map',
      style: this.getMapStyleForLayer('streets'),
      center: [113.9213, -0.7893],
      zoom: 5
    };
  }
  
  getMapStyleForLayer(layerType = 'streets') {
    switch(layerType) {
      case 'satellite':
        return `https://api.maptiler.com/maps/satellite/style.json?key=${CONFIG.MAP_TILER_KEY}`;
      case 'terrain':
        return `https://api.maptiler.com/maps/outdoor-v2/style.json?key=${CONFIG.MAP_TILER_KEY}`;
      case 'dark':
        return `https://api.maptiler.com/maps/streets-v2-dark/style.json?key=${CONFIG.MAP_TILER_KEY}`;
      default:
        return `https://api.maptiler.com/maps/streets-v2/style.json?key=${CONFIG.MAP_TILER_KEY}`;
    }
  }

  clearForm() {
    this.selectedImage = null;
    this.selectedLocation = null;
  }

  updateCurrentOffset(offset) {
    this.currentOffset = offset;
  }

  setHasMorePosts(hasMore) {
    this.hasMorePosts = hasMore;
  }

  resetPagination() {
    this.currentOffset = 0;
    this.hasMorePosts = true;
  }
}